import React, { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AiIcon,
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { prefilledDocumentService } from '../services/prefilledDocumentService';
import { IPrefilledDocument } from '../types/prefilledDocument';
import { stitchColors } from '../theme/tokens';

interface PrefillDocumentModalProps {
  open: boolean;
  sourceDocumentId: string;
  sourceDocumentTitle: string;
  onClose: () => void;
  onCreated: (created: IPrefilledDocument) => void;
}

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const PrefillDocumentModal: React.FC<PrefillDocumentModalProps> = ({
  open,
  sourceDocumentId,
  sourceDocumentTitle,
  onClose,
  onCreated,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    if (loading) return;
    setFile(null);
    setError(null);
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    e.target.value = '';
    if (!selected) return;
    setError(null);
    if (!selected.name.toLowerCase().endsWith('.pdf')) {
      setError('Please select a PDF file.');
      return;
    }
    if (selected.size > MAX_FILE_SIZE_BYTES) {
      setError(`File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
      return;
    }
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const created = await prefilledDocumentService.create(sourceDocumentId, file);
      setFile(null);
      onCreated(created);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to pre-fill the document. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="h6" component="span" fontWeight={600}>
          Pre-fill New File
        </Typography>
        <IconButton onClick={handleClose} disabled={loading} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 2.5 }}>
        <Typography variant="body2" color="text.secondary">
          Upload a blank PDF form. AI fills it using answers from <strong>{sourceDocumentTitle}</strong>.
          Anything it cannot find is left as ___.
        </Typography>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        <input
          ref={fileInputRef}
          type="file"
          data-testid="prefill-file-input"
          accept=".pdf,application/pdf"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        {!file ? (
          <Box
            role="button"
            tabIndex={0}
            aria-label="Choose a PDF file to pre-fill"
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            sx={{
              border: `2px dashed ${stitchColors.outlineVariant}`,
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: stitchColors.surfaceContainerLowest,
              '&:hover': { borderColor: stitchColors.primary, bgcolor: stitchColors.surfaceContainerLow },
              '&:focus-visible': {
                outline: `2px solid ${stitchColors.primary}`,
                outlineOffset: 2,
              },
            }}
          >
            <UploadIcon sx={{ fontSize: 44, color: 'text.secondary', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Click to choose a PDF
            </Typography>
            <Typography variant="body2" color="text.secondary">
              PDF only, max {MAX_FILE_SIZE_MB}MB
            </Typography>
          </Box>
        ) : (
          <Paper
            variant="outlined"
            sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderRadius: 2, bgcolor: stitchColors.surfaceContainerLowest }}
          >
            <PdfIcon sx={{ color: '#d32f2f' }} />
            <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
              {file.name}
            </Typography>
            <IconButton size="small" color="error" onClick={() => setFile(null)} disabled={loading} aria-label="Remove file">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        )}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: stitchColors.surfaceContainerLow, borderRadius: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">
              Reading the PDF and filling in answers. This can take up to a minute...
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!file || loading}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <AiIcon />}
        >
          {loading ? 'Pre-filling...' : 'Pre-fill'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PrefillDocumentModal;
